import { fromApi, isLocalDateTimePast } from '@/utils/datetime';
import type {
  Customer,
  Reservation,
  ReservationUpdateBody,
  StaffReservationCreateBody,
} from '@restaurant/api-client';
import type { OCCASION_VALUES, SEATING_VALUES } from '@restaurant/api-contracts';
import { ReservationSource } from '@restaurant/types';

// Structured special-request options come straight from @restaurant/api-contracts
// (single source of truth - can't drift); labels from the shared labels.ts.
export type Occasion = (typeof OCCASION_VALUES)[number];
export type Seating = (typeof SEATING_VALUES)[number];
export type StaffSource = ReservationSource.PHONE | ReservationSource.WALK_IN;

// Guest-name whitelist (mirrors the storefront's NAME_RE): at least one letter,
// then only letters/marks/spaces and the punctuation real names use (' . -). A
// strict whitelist keeps digits and SQL/HTML metacharacters out of the field.
export const NAME_RE = /^(?=.*\p{L})[\p{L}\p{M}\s'.-]+$/u;
// Email whitelist mirroring the storefront's EMAIL_RE: local part with no @/space,
// then dot-separated domain labels that are each non-empty and dot-free, ending
// in a 2+ LETTER TLD - so consecutive dots ("gmail..com"), a leading/trailing
// domain dot, a bare TLD-less host, AND a 1-char/incomplete TLD ("gmail.c") are
// all rejected client-side with a clear message (matches the backend zod .email).
export const EMAIL_RE = /^[^@\s]+@[^\s@.]+(\.[^\s@.]+)*\.[A-Za-z]{2,}$/;
/** Max guest-name length - matches the contract's `name.max(100)`. */
export const NAME_MAX = 100;
/** Max email length - matches the contract's `email.max(120)`. */
export const EMAIL_MAX = 120;
/** Max internal-note length - matches the contract's `internalNote.max(500)`. */
export const NOTE_MAX = 500;

export interface BookingValues {
  date: string;
  time: string;
  partySize: number;
  name: string;
  email: string;
  phone: string;
  occasion: Occasion[];
  seating: Seating[];
  internalNote: string;
}

export interface CreateValues extends BookingValues {
  source: StaffSource;
  customerId: string | null;
}

export interface EditValues extends BookingValues {
  duration: string;
}

export type FormProblem =
  | 'nameRequired'
  | 'nameInvalid'
  | 'phoneRequired'
  | 'phoneInvalid'
  | 'noRestaurant'
  | 'timeInPast'
  | 'emailInvalid'
  | 'timeRequired';

export function createValues(date: string): CreateValues {
  return {
    date,
    time: '19:00',
    partySize: 2,
    name: '',
    email: '',
    phone: '',
    occasion: [],
    seating: [],
    internalNote: '',
    source: ReservationSource.PHONE,
    customerId: null,
  };
}

export function editValues(reservation: Reservation): EditValues {
  const dt = fromApi(reservation.time);
  return {
    date: dt.toISODate() ?? '',
    time: dt.toFormat('HH:mm'),
    partySize: reservation.partySize,
    duration: String(reservation.duration),
    name: reservation.guest.name,
    email: reservation.guest.email ?? '',
    phone: reservation.guest.phone ?? '',
    occasion: reservation.specialRequest?.occasion ?? [],
    seating: reservation.specialRequest?.seatingPreference ?? [],
    internalNote: reservation.internalNote ?? '',
  };
}

/** Compact label for the booking date chip - matches ChipInput's date format. */
export function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function primaryPhone(customer: Customer): string {
  const active = customer.phoneNumbers.filter((p) => p.isActive);
  return (active.find((p) => p.isPrimary) ?? active[0])?.phone ?? '';
}

export const toggle = <T>(list: readonly T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

// Email is optional, but if the operator types one it must be valid - the
// backend rejects a malformed address (e.g. "a@test") with an opaque 400.
// Mirror the contract's `z.string().email()` intent so we catch it inline.
export function isEmailInvalid(email: string): boolean {
  const trimmed = email.trim();
  return trimmed !== '' && (!EMAIL_RE.test(trimmed) || trimmed.length > EMAIL_MAX);
}

// "Has the operator started a draft?" - true once any field deviates from its
// initial default. Drives the unsaved-changes guard so a refresh / Back / tab
// close while drafting prompts before throwing the half-entered booking away.
export function hasDraft(values: CreateValues, start: CreateValues): boolean {
  return (
    values.name.trim() !== '' ||
    values.email.trim() !== '' ||
    values.phone.trim() !== '' ||
    values.internalNote.trim() !== '' ||
    values.occasion.length > 0 ||
    values.seating.length > 0 ||
    values.customerId !== null ||
    values.source !== start.source ||
    values.date !== start.date ||
    values.time !== start.time ||
    values.partySize !== start.partySize
  );
}

export function createProblem(
  values: CreateValues,
  phoneValid: boolean,
  hasRestaurant: boolean,
): FormProblem | null {
  const phoneBooking = values.source === ReservationSource.PHONE;
  // Only the guest's name is required. Phone and email are optional - a phone
  // booking often yields just a name, and asking for an email mid-call is
  // awkward and usually unnecessary.
  if (!values.name.trim()) return 'nameRequired';
  // A guest name is a strict whitelist: letters (any script, incl. accents),
  // combining marks, spaces, and the few punctuation marks real names use
  // (' . -), and must contain at least one letter. Everything else - digits,
  // quotes, semicolons, angle/brackets, SQL/HTML metacharacters - is rejected,
  // so the field can't carry an injection payload or a mistyped phone/table no.
  if (!NAME_RE.test(values.name.trim())) return 'nameInvalid';
  // Phone booking → a reachable phone is required (that's how staff call the
  // guest back). Walk-in → optional. Either way, a supplied number must be a
  // valid, assignable one (5–20 chars + a real number for its country).
  if (phoneBooking && !values.phone.trim()) return 'phoneRequired';
  if (values.phone.trim() && !phoneValid) return 'phoneInvalid';
  if (!hasRestaurant) return 'noRestaurant';
  // Phone bookings can't be in the past (the backend rejects it with a terse
  // 400) - catch it here with a clear message. Walk-ins are exempt: staff log
  // them as the guest is already seated, sometimes a few minutes late.
  if (phoneBooking && isLocalDateTimePast(values.date, values.time)) return 'timeInPast';
  // Block a malformed email before it reaches the backend's opaque 400. Show
  // the same popup alert as the name/phone checks (a dialog that names what's
  // wrong) - not a toast, which is easy to miss on mobile.
  if (isEmailInvalid(values.email)) return 'emailInvalid';
  return null;
}

export function editProblem(values: EditValues, phoneValid: boolean): FormProblem | null {
  // Name stays required and whitelisted - the backend rejects an empty or
  // number/symbol-laden name, and clearing it here would otherwise 400.
  if (!values.name.trim()) return 'nameRequired';
  if (!NAME_RE.test(values.name.trim())) return 'nameInvalid';
  // A supplied phone must be a valid, assignable number (5–20 chars + a real
  // number for its country), matching the create form.
  if (values.phone.trim() && !phoneValid) return 'phoneInvalid';
  // A supplied email must be well-formed - same whitelist as the create form,
  // so the backend's opaque 400 on a malformed address never triggers.
  if (isEmailInvalid(values.email)) return 'emailInvalid';
  // A cleared desktop time field emits "" (the native input empties its whole
  // value when any segment is cleared) - the contract's HH:mm regex rejects
  // that as an opaque 400, so stop here with a message instead.
  if (!/^\d{2}:\d{2}$/.test(values.time)) return 'timeRequired';
  return null;
}

export function createBody(values: CreateValues): StaffReservationCreateBody {
  // Only include preferences that were actually set, so we don't persist empty
  // arrays on every booking.
  const specialRequest = {
    ...(values.occasion.length ? { occasion: values.occasion } : {}),
    ...(values.seating.length ? { seatingPreference: values.seating } : {}),
  };
  return {
    date: values.date,
    time: values.time,
    partySize: values.partySize,
    guest: {
      name: values.name.trim(),
      email: values.email.trim() || null,
      phone: values.phone.trim() || null,
    },
    source: values.source,
    specialRequest: Object.keys(specialRequest).length ? specialRequest : undefined,
    internalNote: values.internalNote.trim() || undefined,
  };
}

export function editBody(values: EditValues, reservation: Reservation): ReservationUpdateBody {
  const saved = editValues(reservation);
  const body: ReservationUpdateBody = {};
  if (values.date !== saved.date) body.date = values.date;
  if (values.time !== saved.time) body.time = values.time;
  if (values.partySize !== saved.partySize) body.partySize = values.partySize;
  const duration = parseInt(values.duration, 10);
  if (!isNaN(duration) && duration !== reservation.duration) body.duration = duration;
  if (values.name !== saved.name || values.email !== saved.email || values.phone !== saved.phone) {
    body.guest = {
      name: values.name !== saved.name ? values.name : undefined,
      email: values.email !== saved.email ? values.email || undefined : undefined,
      phone: values.phone !== saved.phone ? values.phone || undefined : undefined,
    };
  }
  // Structured preferences (multi-select) - only send the ones that actually
  // changed. An empty array clears the field.
  const specialRequest: NonNullable<ReservationUpdateBody['specialRequest']> = {};
  if (!sameSet(values.occasion, saved.occasion)) specialRequest.occasion = values.occasion;
  if (!sameSet(values.seating, saved.seating)) specialRequest.seatingPreference = values.seating;
  if (Object.keys(specialRequest).length > 0) body.specialRequest = specialRequest;
  if (values.internalNote !== saved.internalNote) body.internalNote = values.internalNote || null;
  return body;
}

export const textInput = (field: { value: string; onChange: (value: string) => void }) => ({
  value: field.value,
  onInput: (event: { target: HTMLInputElement }) => field.onChange(event.target.value),
});
